import { Button, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputLabel, Stack, TextField, styled } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import serverCall from 'api/serverCall';
import api from 'api/api';
import LoadingButton from 'components/@extended/LoadingButton';
import { getConfigValidationSchema } from 'validations/stockValidationSchema';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { IFund, ISector } from 'types/custom/common';

const Required = styled('span')({
  color: 'red',
  marginLeft: '2px'
});

type Type = 'sector' | 'fund' | null;

const getInitialValues = (data: ISector | IFund | null, type: Type) => {
  const defaultValues = {
    sector: { sectorName: '', description: '' },
    fund: { fundName: '', description: '' }
  };

  if (data) {
    if (type === 'sector') {
      const sectorData = data as ISector;
      return {
        sectorName: sectorData?.sectorName || '',
        description: sectorData?.description || ''
      };
    } else if (type === 'fund') {
      const fundData = data as IFund;
      return {
        fundName: fundData?.fundName || '',
        description: fundData?.description || ''
      };
    }
  }
  return type === 'sector' ? defaultValues.sector : defaultValues.fund;
};

export interface Props {
  data: ISector | IFund | null;
  type: Type;
  onCancel: () => void;
  fetchData: () => void;
}

const AddConfig = ({ onCancel, data, type, fetchData }: Props) => {
  const formik = useFormik({
    initialValues: getInitialValues(data, type),
    validationSchema: getConfigValidationSchema(type!),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);
        let requestBody: any = null;
        let updateId = '';
        if (type === 'sector') {
          updateId = (data as ISector)?.sectorId || '';
          requestBody = {
            sectorName: values.sectorName,
            description: values.description
          };
        } else if (type === 'fund') {
          updateId = (data as IFund)?.fundId || '';
          requestBody = {
            fundName: values.fundName,
            description: values.description
          };
        }
        const apiCall = data
          ? new Promise((resolve, reject) => {
              serverCall.put(type === 'sector' ? api.updateSector(updateId) : api.updateFund(updateId), requestBody, (response, error) => {
                if (error) {
                  reject(error);
                } else {
                  resolve(response);
                }
              });
            })
          : new Promise((resolve, reject) => {
              serverCall.post(type === 'sector' ? api.saveSector() : api.saveFund(), requestBody, (response, error) => {
                if (error) {
                  reject(error);
                } else {
                  resolve(response);
                }
              });
            });

        apiCall
          .then((response) => {
            dispatch(
              openSnackbar({
                open: true,
                close: true,
                message: `Successfully ${data ? 'updated' : 'created'}`,
                variant: 'alert',
                alert: {
                  color: 'success'
                },
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'right'
                }
              })
            );
            onCancel();
            resetForm();
            fetchData();
          })
          .catch((error) => {
            const errorMessage = error?.response?.data?.message || `Failed to ${data ? 'update' : 'create'}`;
            dispatch(
              openSnackbar({
                open: true,
                close: true,
                message: errorMessage,
                variant: 'alert',
                alert: {
                  color: 'error'
                },
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'right'
                }
              })
            );
          })
          .finally(() => {
            setSubmitting(false);
          });
      } catch (error) {
        setSubmitting(false);
        console.error(error);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;
  const title =
    type === 'sector' && data
      ? 'Edit Sector'
      : type === 'sector' && !data
      ? 'Add Sector'
      : type === 'fund' && data
      ? 'Edit Fund'
      : 'Add Fund';

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Grid container spacing={3}>
                {type === 'sector' ? (
                  <Grid item xs={12}>
                    <Stack spacing={1.25}>
                      <InputLabel htmlFor="sector-name">
                        Sector Name
                        <Required>*</Required>
                      </InputLabel>
                      <TextField
                        fullWidth
                        id="sector-name"
                        placeholder="Enter Sector Name"
                        {...getFieldProps('sectorName')}
                        error={Boolean(touched.sectorName && errors.sectorName)}
                        helperText={touched.sectorName && errors.sectorName}
                      />
                    </Stack>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Stack spacing={1.25}>
                      <InputLabel htmlFor="fund-name">
                        Fund Name
                        <Required>*</Required>
                      </InputLabel>
                      <TextField
                        fullWidth
                        id="fund-name"
                        placeholder="Enter Fund Name"
                        {...getFieldProps('fundName')}
                        error={Boolean(touched.fundName && errors.fundName)}
                        helperText={touched.fundName && errors.fundName}
                      />
                    </Stack>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="group-description">Description</InputLabel>
                    <TextField
                      fullWidth
                      multiline
                      id="group-description"
                      placeholder="Enter Description"
                      rows={5}
                      {...getFieldProps('description')}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Grid container justifyContent="end" alignItems="center">
            <Grid item>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button color="error" onClick={onCancel}>
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  loading={isSubmitting}
                  fullWidth
                  disabled={isSubmitting}
                  variant="contained"
                  style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                >
                  <span style={{ marginLeft: isSubmitting ? '20px' : '' }}>{isSubmitting ? 'Saving... ' : 'Save'}</span>
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </DialogActions>
      </Form>
    </FormikProvider>
  );
};

export default AddConfig;
